import { useRef, useState, useEffect } from "react";
import { ModalProps } from "antd";
import { FiCheckCircle } from "react-icons/fi";

import {
  Input,
  Container,
  ContentButton,
  HeaderDescription,
  ContentAnimals,
  ListAnimals,
  ContentBatch,
  BoxChechedAnimals,
} from "./styles";
import { ModalBaseAnimal } from "../ModalBaseAnimal";
import { Button } from "../Button";
import {
  IAnimal,
  IBatchOrFlock,
  SelectAnimal,
} from "../../modules/animals/interface";
import { ItemAnimal } from "../ItemAnimal";
import { useNotification } from "../../hooks/useNotification";
import { ControlCheckbox as Checkbox } from "../../components/Checkbox";
import { useTranslate } from "../../hooks/useTranslate";

type CreateFlockOptions = "animals" | "all-animals" | "by-batch" | "";

type Props = {
  animals: IAnimal[];
  batchs: IBatchOrFlock[];
  cb: (name: string, animals: string[]) => Promise<void>;
} & ModalProps;

const ModalFlock = ({ animals, batchs, cb, visible, ...rest }: Props) => {
  const { translate } = useTranslate();
  const { errorNotification } = useNotification();

  const nameFlock = useRef<HTMLInputElement>(null);
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [selectedBatchs, setSelectedBatchs] = useState<string[]>([]);
  const [option, setOption] = useState<CreateFlockOptions>("");

  const handleClickAnimal = ({ value, id }: SelectAnimal) => {
    if (value) {
      setSelectedAnimals((oldState) => [...oldState, id]);
      return;
    }

    const selectedAnimalsFiltered = selectedAnimals.filter(
      (item) => item !== id
    );
    setSelectedAnimals(selectedAnimalsFiltered);
  };

  const selectAllAnimals = () => {
    const allAnimals = animals.map((animal) => animal.id);
    setSelectedAnimals(allAnimals);
  };

  const handleClickBatch = (id: string) => {
    let batchs = selectedBatchs;
    const index = selectedBatchs.indexOf(id);

    if (index >= 0) {
      batchs = selectedBatchs.filter((batch) => batch !== id);
    } else {
      batchs.push(id);
    }

    setSelectedBatchs([...batchs]);
    const animalsByBatch = animals.filter((animal) =>
      batchs.includes(animal.batch?.id || "")
    );
    const ids = animalsByBatch.map((animal) => animal.id);
    setSelectedAnimals(ids);
  };

  const submit = () => {
    const name = nameFlock.current?.value || "";

    if (!name) {
      errorNotification(translate("animals.name_flock_required"));
      return;
    }

    cb(name, selectedAnimals);

    setOption("");
    setSelectedAnimals([]);
    setSelectedBatchs([]);
    if (nameFlock.current?.value) nameFlock.current.value = "";
  };

  useEffect(() => {
    setOption("");
    setSelectedAnimals([]);
    setSelectedBatchs([]);
    if (nameFlock.current?.value) nameFlock.current.value = "";
  }, [visible]);

  return (
    <ModalBaseAnimal
      visible={visible}
      {...rest}
      title={translate("animals.title_new_flock")}
    >
      <Input
        ref={nameFlock}
        placeholder={translate("global.placeholder_name")}
      />
      <Container>
        <HeaderDescription>
          <p>{translate("animals.animal_plural")}</p>
          <span>{animals.length}</span>
        </HeaderDescription>
        {!option && (
          <>
            {animals.length > 0 && (
              <>
                <ContentAnimals>
                  <button
                    onClick={() => {
                      setOption("animals");
                    }}
                  >
                    {translate("animals.select_animal")}
                  </button>
                </ContentAnimals>
                <ContentAnimals>
                  <button
                    onClick={() => {
                      setOption("all-animals");
                      selectAllAnimals();
                    }}
                  >
                    {translate("animals.select_all_animals")}
                  </button>
                </ContentAnimals>
              </>
            )}

            {batchs.length > 0 && (
              <ContentAnimals>
                <button
                  onClick={() => {
                    setOption("by-batch");
                  }}
                >
                  {translate("animals.select_batch")}
                </button>
              </ContentAnimals>
            )}

            {batchs.length === 0 && animals.length === 0 && (
              <span>{translate("animals.without_batchs")}</span>
            )}
          </>
        )}

        {option === "animals" && (
          <ListAnimals>
            {animals.map((animal) => (
              <ItemAnimal
                key={animal.id}
                animal={{
                  ...animal,
                  selected: selectedAnimals.includes(animal.id),
                }}
                editable
                handleClick={handleClickAnimal}
              />
            ))}
          </ListAnimals>
        )}

        {option === "all-animals" && (
          <BoxChechedAnimals>
            <p>{translate("animals.selected_all_animals")}</p>
            <FiCheckCircle />
          </BoxChechedAnimals>
        )}

        {option === "by-batch" && batchs.length > 0 && (
          <ListAnimals>
            {batchs.map((batch) => (
              <ContentBatch
                key={batch.id}
                onClick={() => {
                  handleClickBatch(batch.id);
                }}
              >
                <p>
                  {batch.name} -{" "}
                  <span>
                    {batch.count}{" "}
                    {batch.count === 1
                      ? translate("animals.animal_singular")
                      : translate("animals.animal_plural")}
                  </span>
                </p>
                <Checkbox checked={selectedBatchs.includes(batch.id)} />
              </ContentBatch>
            ))}
          </ListAnimals>
        )}
      </Container>
      <ContentButton>
        <Button
          label={translate("animals.label_btn_new_flock")}
          type="button"
          onClick={submit}
        />
      </ContentButton>
    </ModalBaseAnimal>
  );
};

export { ModalFlock };
